import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { LeadV3Source } from 'models/LeadsV3';
import { LeadSource, LeadStatus } from 'models/Leads';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { countryPhoneCodes } from 'utils/constants/countryPhoneCodes';

const getPhoneCountryCodeObject = (
  option: { value: string } | string | null,
) => {
  const countryCode = typeof option === 'string' ? option : option?.value;
  return countryPhoneCodes.find(
    ({ code, dial_code }) => code === countryCode || dial_code === countryCode,
  );
};

const getPhoneValidator = (t: TFunction, countryCodeName: string) => {
  return Yup.string()
    .test({
      name: 'invalid-phone-number',
      message: t('form.validation.invalidPhone'),
      test: (phoneNumber, testContext) => {
        // If not yet filled, do not validate
        if (!phoneNumber) {
          return true;
        }

        const phoneCountryCode = getPhoneCountryCodeObject(
          testContext.parent[`${countryCodeName}_countryCode`],
        );

        const parsedPhoneNumber = parsePhoneNumberFromString(
          phoneNumber,
          phoneCountryCode?.code as CountryCode,
        );

        // If it failed to parse, then it's not a valid phone number
        if (!parsedPhoneNumber) {
          return false;
        }

        return parsedPhoneNumber.isValid();
      },
    })
    .optional();
};

const leadFormSchema = (t: TFunction) => ({
  adultCount: Yup.number(),
  childrenCount: Yup.number(),
  checkInDate: Yup.date()
    .required()
    .typeError(t('form.validation.invalidDate')),
  checkOutDate: Yup.date()
    .required()
    .typeError(t('form.validation.invalidDate'))
    .min(
      Yup.ref('checkInDate'),
      t('componentLead.modal.form.validation.checkOutDateInvalid'),
    ),
  email: Yup.string()
    .email(t('form.validation.invalidEmail'))
    .typeError(t('form.validation.invalidEmail'))
    .when('source', {
      // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      is: (src) => src !== LeadSource.DIRECT_AIRBNB,
      then: Yup.string().required(t('form.validation.requiredEmail')),
      otherwise: Yup.string().notRequired().nullable(),
    }),
  firstName: Yup.string(),
  lastName: Yup.string(),
  propertyUid: Yup.string(),
  source: Yup.mixed<LeadV3Source | LeadSource>().oneOf([
    ...Object.values(LeadV3Source),
    ...Object.values(LeadSource),
  ]),
  status: Yup.mixed<LeadStatus>().oneOf(Object.values(LeadStatus)),
  uid: Yup.string(),
  phoneNumber_base: getPhoneValidator(t, 'phoneNumber'),
  cellPhoneNumber_base: getPhoneValidator(t, 'cellPhoneNumber'),
  // TODO add other form values
});

export default leadFormSchema;
